// Admin "Comptabilité" — accounting cockpit (2026-06-18).
//
// A MANAGEMENT cockpit for cash, margins and targets — NOT statutory / tax /
// double-entry. Revenue recognized on delivery. COGS + contribution are READ
// from the worker's /accounting/summary (which reuses order_costs + the
// dashboard's isDelivered semantics). The only writable surface is the manual
// expenses ledger (recurring / variable / one_time) + the daily profit target.
//
// Premium cream/gold cockpit: semantic brand tokens, SVG icons, large Fraunces
// money numbers (positive = sage, negative = terracotta), single tasteful
// load-stagger via .adm-rise (respects prefers-reduced-motion), mobile-first.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import {
  accountingApi,
  ordersApi,
  type AccountingSummary,
  type Expense,
  type ExpenseKind,
  type AccountingExpenseInput,
  type AmeexPayout,
  type CashAdjustment,
  type Order,
} from '@/lib/api';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import { ExpenseReceiptModal } from '@/components/admin/ExpenseReceiptModal';
import { MarketingSection } from '@/components/admin/MarketingSection';
import { DateRangePicker, presetRange, type DateRange } from '@/components/admin/DateRangePicker';
import { ADM_COLORS as COLORS, withAlpha } from '@/lib/brand';
import { useT } from '@/lib/i18n';

const HEADING = "'Fraunces', 'Cormorant Garamond', Georgia, serif";
const cardStyle = {
  background: COLORS.bgCard,
  borderColor: COLORS.borderSubtle,
  boxShadow: '0 1px 3px rgba(46,36,24,0.04), 0 8px 24px rgba(46,36,24,0.05)',
} as const;

const mad = (n: number) =>
  `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(n))} DH`;
const madPrecise = (n: number) =>
  `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(n)} DH`;
const moneyColor = (n: number) =>
  n > 0 ? COLORS.success : n < 0 ? COLORS.error : COLORS.textSecondary;

const CATEGORY_LABELS: Record<string, string> = {
  rent: 'Loyer',
  electricity: 'Électricité',
  wifi: 'Internet / Wifi',
  salaries: 'Salaires',
  software: 'Logiciels',
  machine: 'Machine / Matériel',
  ads: 'Publicité',
  other: 'Autre',
};
const KIND_LABELS: Record<ExpenseKind, string> = {
  recurring: 'Récurrente',
  variable: 'Variable',
  one_time: 'Ponctuelle',
};

// ─── SVG icon helper ─────────────────────────────────────────────────────────
function Icon({ d, className = 'w-[18px] h-[18px]' }: { d: React.ReactNode; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {d}
    </svg>
  );
}
const ICONS = {
  revenue: <><path d="M12 1v22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>,
  margin: <><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 5-6" /></>,
  contribution: <><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9 9.5h4.5a1.5 1.5 0 0 1 0 3H10a1.5 1.5 0 0 0 0 3H15" /></>,
  net: <><path d="M3 12h4l3-9 4 18 3-9h4" /></>,
  cash: <><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /><path d="M6 12h.01M18 12h.01" /></>,
  wallet: <><path d="M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1" /><path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6" /><circle cx="16.5" cy="13" r="1.2" /></>,
  plus: <><path d="M12 5v14M5 12h14" /></>,
  trash: <><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></>,
  target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></>,
};

// ─── KPI card ────────────────────────────────────────────────────────────────
function KpiCard({
  icon,
  label,
  value,
  sub,
  color = COLORS.textPrimary,
  accent = false,
  delay = 0,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color?: string;
  accent?: boolean;
  delay?: number;
}) {
  return (
    <div
      className="rounded-xl p-5 border relative overflow-hidden adm-rise transition-transform duration-200 hover:-translate-y-0.5"
      style={{ ...cardStyle, animationDelay: `${delay}ms` }}
    >
      {accent && (
        <span
          aria-hidden
          className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ background: `linear-gradient(90deg, ${COLORS.accentPrimary}, #E0BE73)` }}
        />
      )}
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
        style={{ background: COLORS.accentSoft, color: COLORS.accentHover }}
      >
        <Icon d={icon} />
      </div>
      <p className="text-[11px] font-medium uppercase tracking-wider mb-1.5" style={{ color: COLORS.textTertiary }}>
        {label}
      </p>
      <p
        className="text-[26px] leading-none mb-1"
        style={{ color, fontFamily: HEADING, fontWeight: 500, letterSpacing: '-0.01em' }}
      >
        {value}
      </p>
      {sub && (
        <p className="text-[11px] mt-1.5" style={{ color: COLORS.textTertiary }}>
          {sub}
        </p>
      )}
    </div>
  );
}

function SectionHeading({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 mb-3 mt-7">
      <span className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: COLORS.accentHover }}>
        {label}
      </span>
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${withAlpha(COLORS.accentPrimary, '55')}, transparent)` }} />
    </div>
  );
}

// A single P&L ledger row. Negative lines are subtractions.
function PnlRow({ label, value, subtract = false, strong = false, hint }: { label: string; value: number; subtract?: boolean; strong?: boolean; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between py-2 border-b last:border-b-0" style={{ borderColor: COLORS.borderSubtle }}>
      <div>
        <span className={strong ? 'text-sm font-semibold' : 'text-sm'} style={{ color: strong ? COLORS.textPrimary : COLORS.textSecondary }}>
          {subtract ? '− ' : ''}
          {label}
        </span>
        {hint && <span className="ml-2 text-[11px]" style={{ color: COLORS.textTertiary }}>{hint}</span>}
      </div>
      <span
        className={strong ? 'text-base' : 'text-sm'}
        style={{
          fontFamily: strong ? HEADING : undefined,
          fontWeight: strong ? 500 : 600,
          color: strong ? moneyColor(value) : subtract ? COLORS.error : COLORS.textPrimary,
        }}
      >
        {subtract ? '−' : ''}
        {mad(Math.abs(value))}
      </span>
    </div>
  );
}

function Card({ title, children, delay = 0 }: { title: string; children: React.ReactNode; delay?: number }) {
  return (
    <div className="rounded-xl border overflow-hidden adm-rise" style={{ ...cardStyle, animationDelay: `${delay}ms` }}>
      <div className="px-5 py-4 border-b" style={{ borderColor: COLORS.borderSubtle }}>
        <h3 className="text-lg" style={{ fontFamily: HEADING, color: COLORS.textPrimary, fontWeight: 500 }}>
          {title}
        </h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ─── Sparkline (mini bar chart of daily contribution) ────────────────────────
function Sparkline({ byDay }: { byDay: AccountingSummary['byDay'] }) {
  const { t } = useT();
  if (!byDay.length) return null;
  const max = Math.max(...byDay.map((d) => Math.abs(d.profit)), 1);
  return (
    <div className="flex items-end gap-1 h-20">
      {byDay.map((d) => {
        const h = Math.max(3, Math.round((Math.abs(d.profit) / max) * 76));
        const pos = d.profit >= 0;
        return (
          <div
            key={d.day}
            className="flex-1 rounded-t min-w-[2px]"
            style={{ height: h, background: pos ? COLORS.success : COLORS.error, opacity: 0.85 }}
            title={`${d.day} — ${t('contribution')} ${madPrecise(d.profit)}`}
          />
        );
      })}
    </div>
  );
}

// ─── Targets progress bar ────────────────────────────────────────────────────
function ProgressRow({ label, p }: { label: string; p: AccountingSummary['targets']['progress']['today'] }) {
  const { t } = useT();
  const pct = p.progress != null ? Math.max(0, Math.min(1.4, p.progress)) : 0;
  const onTrack = p.gap >= 0;
  return (
    <div className="py-2">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{label}</span>
        <span className="text-xs" style={{ color: COLORS.textSecondary }}>
          {mad(p.realized)} / {mad(p.goal)}
        </span>
      </div>
      <div className="h-2.5 w-full rounded-full overflow-hidden" style={{ background: COLORS.bgElevated }}>
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${Math.min(100, pct * 100)}%`,
            background: onTrack
              ? `linear-gradient(90deg, ${COLORS.success}, #97A98A)`
              : `linear-gradient(90deg, ${COLORS.warning}, #E0B377)`,
          }}
        />
      </div>
      <p className="text-[11px] mt-1" style={{ color: moneyColor(p.gap) }}>
        {p.gap >= 0 ? `+${mad(p.gap)} ${t('d’avance')}` : `${mad(p.gap)} ${t('d’écart')}`}
      </p>
    </div>
  );
}

// ─── Expense form modal (manual entry, also reused for edit) ──────────────────
const fInput = 'w-full px-2.5 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 bg-white';
const fStyle = { borderColor: COLORS.borderSubtle } as const;

function ExpenseFormModal({ existing, onClose }: { existing?: Expense; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [kind, setKind] = useState<ExpenseKind>(existing?.kind ?? 'recurring');
  const [category, setCategory] = useState(existing?.category ?? 'rent');
  const [amount, setAmount] = useState(existing ? String(existing.amount) : '');
  const [date, setDate] = useState(existing?.expense_date ?? new Date().toISOString().slice(0, 10));
  const [vendor, setVendor] = useState(existing?.vendor ?? '');
  const [label, setLabel] = useState(existing?.label ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');

  const save = useMutation({
    mutationFn: () => {
      const payload: AccountingExpenseInput = {
        kind,
        category,
        amount: Number(amount),
        expense_date: date,
        vendor: vendor || undefined,
        label: label || undefined,
        notes: notes || undefined,
      };
      return existing ? accountingApi.updateExpense(existing.id, payload) : accountingApi.addExpense(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounting-summary'] });
      qc.invalidateQueries({ queryKey: ['accounting-expenses'] });
      onClose();
    },
  });

  const valid = Number(amount) >= 0 && /^\d{4}-\d{2}-\d{2}$/.test(date) && !!category;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(17,17,17,0.10)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[480px] max-h-[90vh] overflow-y-auto rounded-2xl p-5 shadow-2xl adm-rise"
        style={{ background: COLORS.bgCard }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 mb-4">
          <h3 className="text-[16px] font-semibold" style={{ fontFamily: HEADING, color: COLORS.textPrimary }}>
            {existing ? t('Modifier la dépense') : t('Ajouter une dépense')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Fermer')}
            className="ml-auto w-8 h-8 rounded-lg border flex items-center justify-center"
            style={{ borderColor: COLORS.borderSubtle, color: COLORS.textTertiary }}
          >
            ✕
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: COLORS.textSecondary }}>{t('Type')}</label>
            <div className="grid grid-cols-3 gap-2">
              {(['recurring', 'variable', 'one_time'] as ExpenseKind[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className="py-2 rounded-lg text-xs font-semibold border transition-colors"
                  style={
                    kind === k
                      ? { background: COLORS.accentSoft, borderColor: COLORS.accentPrimary, color: COLORS.accentHover }
                      : { background: COLORS.bgCard, borderColor: COLORS.borderSubtle, color: COLORS.textSecondary }
                  }
                >
                  {t(KIND_LABELS[k])}
                </button>
              ))}
            </div>
            {kind === 'one_time' && (
              <p className="text-[11px] mt-1.5" style={{ color: COLORS.warning }}>
                {t('Investissement ponctuel — exclu du bénéfice net mensuel.')}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Catégorie')}</label>
              <select className={fInput} style={fStyle} value={category} onChange={(e) => setCategory(e.target.value)}>
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{t(l)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Montant (MAD)')}</label>
              <input className={fInput} style={fStyle} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Date')}</label>
              <input className={fInput} style={fStyle} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Fournisseur')}</label>
              <input className={fInput} style={fStyle} value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder={t('Optionnel')} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Libellé')}</label>
            <input className={fInput} style={fStyle} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('Optionnel')} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Notes')}</label>
            <input className={fInput} style={fStyle} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('Optionnel')} />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: COLORS.borderSubtle }}>
            <Button variant="ghost" size="sm" onClick={onClose}>{t('Annuler')}</Button>
            <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>
              {existing ? t('Enregistrer') : t('Ajouter')}
            </Button>
          </div>
          {save.isError && <p className="text-xs" style={{ color: COLORS.error }}>{(save.error as Error).message}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── "Dépenser" quick popover (click the Cash en main card) ───────────────────
// Records a NEGATIVE cash_adjustment → the cash box drops immediately. This is a
// CASH spend (paying for stuff), NOT a P&L expense — rent/recurring never touch it.
function SpendPopover({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [label, setLabel] = useState('');

  const spend = useMutation({
    mutationFn: () =>
      accountingApi.addAdjustment({
        amount: -Math.abs(Number(amount)),
        label: label || 'Dépense caisse',
        entry_date: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounting-summary'] });
      qc.invalidateQueries({ queryKey: ['accounting-adjustments'] });
      onClose();
    },
  });
  const valid = Number(amount) > 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center p-4"
      style={{ background: 'rgba(17,17,17,0.10)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[380px] rounded-2xl p-5 shadow-2xl adm-rise"
        style={{ background: COLORS.bgCard }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: COLORS.accentSoft, color: COLORS.accentHover }}>
            <Icon d={ICONS.cash} className="w-[18px] h-[18px]" />
          </div>
          <h3 className="text-[16px] font-semibold" style={{ fontFamily: HEADING, color: COLORS.textPrimary }}>
            {t('Dépenser de la caisse')}
          </h3>
          <button type="button" onClick={onClose} aria-label={t('Fermer')} className="ml-auto w-8 h-8 rounded-lg border flex items-center justify-center" style={{ borderColor: COLORS.borderSubtle, color: COLORS.textTertiary }}>✕</button>
        </div>
        <p className="text-[11px] mb-4" style={{ color: COLORS.textTertiary }}>
          {t('Enregistre une dépense rapide — la caisse baisse aussitôt.')}
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Montant (MAD)')}</label>
            <input
              autoFocus
              className={fInput}
              style={fStyle}
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="120"
              onKeyDown={(e) => { if (e.key === 'Enter' && valid) spend.mutate(); }}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Libellé')}</label>
            <input
              className={fInput}
              style={fStyle}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('ex. table, taxi, café')}
              onKeyDown={(e) => { if (e.key === 'Enter' && valid) spend.mutate(); }}
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={onClose}>{t('Annuler')}</Button>
            <Button size="sm" onClick={() => spend.mutate()} loading={spend.isPending} disabled={!valid}>{t('Dépenser')}</Button>
          </div>
          {spend.isError && <p className="text-xs" style={{ color: COLORS.error }}>{(spend.error as Error).message}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── "Gérer la caisse" — manual cash entry (payouts, advances, adjustments) ───
function ManageCaisseModal({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['accounting-summary'] });
    qc.invalidateQueries({ queryKey: ['accounting-payouts'] });
    qc.invalidateQueries({ queryKey: ['accounting-adjustments'] });
  };

  const payoutsQ = useQuery({ queryKey: ['accounting-payouts'], queryFn: () => accountingApi.listPayouts() });
  const adjustmentsQ = useQuery({ queryKey: ['accounting-adjustments'], queryFn: () => accountingApi.listAdjustments() });
  const payouts = payoutsQ.data?.payouts ?? [];
  const adjustments = adjustmentsQ.data?.adjustments ?? [];

  const today = new Date().toISOString().slice(0, 10);

  // ── AMEEX payout (single) ──
  const [pAmount, setPAmount] = useState('');
  const [pDate, setPDate] = useState(today);
  const [pRef, setPRef] = useState('');
  const PAYOUT_FEE = 5; // display-only preview; the worker uses app_settings.ameex_payout_fee
  const payoutNet = Math.max(0, Number(pAmount) - PAYOUT_FEE);
  const addPayout = useMutation({
    mutationFn: () => accountingApi.addPayout({ amount: Number(pAmount), received_at: pDate, payout_ref: pRef || undefined }),
    onSuccess: () => { setPAmount(''); setPRef(''); invalidate(); },
  });

  // ── Paste from Portefeuille ──
  const [paste, setPaste] = useState('');
  const parsedRows = paste
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      // "amount date" (date optional → today). Tolerate currency/extra tokens.
      const numMatch = line.match(/-?\d[\d\s.,]*\d|\d/);
      const dateMatch = line.match(/\d{4}-\d{2}-\d{2}/);
      const amount = numMatch ? Math.abs(Number(numMatch[0].replace(/\s/g, '').replace(',', '.'))) : NaN;
      return { amount, received_at: dateMatch ? dateMatch[0] : today };
    })
    .filter((r) => Number.isFinite(r.amount) && r.amount > 0);
  const importPayouts = useMutation({
    mutationFn: () => accountingApi.importPayouts(parsedRows.map((r) => ({ amount: r.amount, received_at: r.received_at }))),
    onSuccess: () => { setPaste(''); invalidate(); },
  });

  // ── Advance client received ──
  const [aAmount, setAAmount] = useState('');
  const [aDate, setADate] = useState(today);
  // Order picker (2026-07-21) — links the advance to the order it belongs to.
  // Searchable (order # / customer name via the existing orders list search),
  // never free text, so the stored order_id always points at a real order.
  const [aOrderSearch, setAOrderSearch] = useState('');
  const [aOrderOpen, setAOrderOpen] = useState(false);
  const [aOrder, setAOrder] = useState<Order | null>(null);
  // Click the field → recent orders drop down immediately; every keystroke
  // (from the FIRST character, "#" tolerated) narrows by order # or customer
  // name. Selection only — free text can never be stored.
  const aOrderTerm = aOrderSearch.trim().replace(/^#/, '');
  const orderMatchesQ = useQuery({
    queryKey: ['caisse-order-search', aOrderTerm],
    queryFn: () => ordersApi.list(aOrderTerm ? { search: aOrderTerm, limit: '8' } : { limit: '8' }),
    enabled: aOrderOpen && !aOrder,
    staleTime: 30_000,
  });
  const addAdvance = useMutation({
    mutationFn: () => accountingApi.addAdjustment({
      amount: Math.abs(Number(aAmount)),
      label: 'Avance client',
      entry_date: aDate,
      ...(aOrder ? { order_id: aOrder.id } : {}),
    }),
    onSuccess: () => { setAAmount(''); setAOrder(null); setAOrderSearch(''); invalidate(); },
  });

  // ── Manual adjustment (signed) ──
  const [adjAmount, setAdjAmount] = useState('');
  const [adjLabel, setAdjLabel] = useState('');
  const addAdjustment = useMutation({
    mutationFn: () => accountingApi.addAdjustment({ amount: Number(adjAmount), label: adjLabel, entry_date: today }),
    onSuccess: () => { setAdjAmount(''); setAdjLabel(''); invalidate(); },
  });

  const delPayout = useMutation({ mutationFn: (id: string) => accountingApi.deletePayout(id), onSuccess: invalidate });
  const delAdjustment = useMutation({ mutationFn: (id: string) => accountingApi.deleteAdjustment(id), onSuccess: invalidate });

  const blockStyle = { background: COLORS.bgPrimary } as const;
  const miniBtn = 'w-7 h-7 rounded-md border flex items-center justify-center shrink-0';

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(17,17,17,0.10)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      {/* Fixed-size centred modal: capped at 88vh as a flex column so the card
          never outgrows the viewport (the old my-6 + no-max-height card grew
          taller than the screen and the items-center overlay trapped the top
          off-screen). Header stays pinned; the three blocks + their long
          payout / adjustment lists scroll INSIDE the modal — the page itself
          never scrolls. Mirrors ExpenseFormModal's max-h approach. */}
      <div
        className="w-full max-w-[620px] max-h-[88vh] flex flex-col rounded-2xl p-5 shadow-2xl adm-rise"
        style={{ background: COLORS.bgCard }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 mb-4 shrink-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: COLORS.accentSoft, color: COLORS.accentHover }}>
            <Icon d={ICONS.wallet} className="w-[18px] h-[18px]" />
          </div>
          <h3 className="text-[16px] font-semibold" style={{ fontFamily: HEADING, color: COLORS.textPrimary }}>{t('Gérer la caisse')}</h3>
          <button type="button" onClick={onClose} aria-label={t('Fermer')} className="ml-auto w-8 h-8 rounded-lg border flex items-center justify-center" style={{ borderColor: COLORS.borderSubtle, color: COLORS.textTertiary }}>✕</button>
        </div>

        {/* Scroll region — min-h-0 lets this flex child shrink so overflow-y
            engages instead of stretching the modal. -mr-2/pr-2 keeps the
            scrollbar off the content. */}
        <div className="flex-1 min-h-0 overflow-y-auto -mr-2 pr-2">
        {/* Versement AMEEX */}
        <div className="rounded-xl p-4 mb-3" style={blockStyle}>
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2.5" style={{ color: COLORS.textTertiary }}>{t('+ Versement AMEEX reçu')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <input className={fInput} style={fStyle} type="number" min="0" step="0.01" placeholder={t('Montant brut')} value={pAmount} onChange={(e) => setPAmount(e.target.value)} />
            <input className={fInput} style={fStyle} type="date" value={pDate} onChange={(e) => setPDate(e.target.value)} />
            <input className={fInput} style={fStyle} placeholder={t('Réf (optionnel)')} value={pRef} onChange={(e) => setPRef(e.target.value)} />
            <Button size="sm" onClick={() => addPayout.mutate()} loading={addPayout.isPending} disabled={!(Number(pAmount) > 0)}>{t('Ajouter')}</Button>
          </div>
          {Number(pAmount) > 0 && (
            <p className="text-[11px] mt-2" style={{ color: COLORS.textSecondary }}>
              {t('Net estimé')}: <span style={{ color: COLORS.success, fontWeight: 600 }}>{madPrecise(payoutNet)}</span>
              <span style={{ color: COLORS.textTertiary }}> ({t('après')} {madPrecise(PAYOUT_FEE)} {t('de frais')})</span>
            </p>
          )}
          {addPayout.isError && <p className="text-[11px] mt-1" style={{ color: COLORS.error }}>{(addPayout.error as Error).message}</p>}

          {/* Paste from Portefeuille */}
          <details className="mt-3">
            <summary className="text-[11px] font-semibold cursor-pointer" style={{ color: COLORS.accentHover }}>{t('Coller du Portefeuille (plusieurs lignes)')}</summary>
            <div className="mt-2">
              <textarea
                className={`${fInput} font-mono text-[11px]`}
                style={{ ...fStyle, minHeight: 90 }}
                placeholder={`1200 2026-06-20\n850 2026-06-22`}
                value={paste}
                onChange={(e) => setPaste(e.target.value)}
              />
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[11px]" style={{ color: COLORS.textTertiary }}>{t('{n} ligne(s) détectée(s)', { n: parsedRows.length })}</span>
                <Button size="sm" variant="secondary" className="ml-auto" onClick={() => importPayouts.mutate()} loading={importPayouts.isPending} disabled={parsedRows.length === 0}>{t('Importer')}</Button>
              </div>
              {importPayouts.isError && <p className="text-[11px] mt-1" style={{ color: COLORS.error }}>{(importPayouts.error as Error).message}</p>}
            </div>
          </details>

          {/* Payout list */}
          {payouts.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {payouts.map((p: AmeexPayout) => (
                <div key={p.id} className="flex items-center gap-2 text-[12px]">
                  <span style={{ color: COLORS.textTertiary }}>{p.received_at}</span>
                  <span style={{ color: COLORS.textSecondary }}>{madPrecise(p.amount)}</span>
                  <span style={{ color: COLORS.error }}>− {madPrecise(p.fee)}</span>
                  <span className="font-semibold" style={{ color: COLORS.success }}>= {madPrecise(p.net)}</span>
                  {p.payout_ref && <span className="truncate" style={{ color: COLORS.textTertiary }}>· {p.payout_ref}</span>}
                  <button onClick={() => delPayout.mutate(p.id)} className={`${miniBtn} ml-auto`} style={{ borderColor: COLORS.borderSubtle, color: COLORS.error }} aria-label={t('Supprimer')}>
                    <Icon d={ICONS.trash} className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Avance client reçue */}
        <div className="rounded-xl p-4 mb-3" style={blockStyle}>
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2.5" style={{ color: COLORS.textTertiary }}>{t('+ Avance client reçue')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <input className={fInput} style={fStyle} type="number" min="0" step="0.01" placeholder={t('Montant')} value={aAmount} onChange={(e) => setAAmount(e.target.value)} />
            <input className={fInput} style={fStyle} type="date" value={aDate} onChange={(e) => setADate(e.target.value)} />
            <Button size="sm" onClick={() => addAdvance.mutate()} loading={addAdvance.isPending} disabled={!(Number(aAmount) > 0)}>{t('Ajouter')}</Button>
          </div>
          {/* Order link — selected chip OR search box + matches dropdown. */}
          <div className="mt-2 relative">
            {aOrder ? (
              <div className="inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold"
                style={{ background: 'rgba(201,162,74,.10)', border: '1px solid rgba(201,162,74,.35)', color: COLORS.textPrimary }}>
                <span>{aOrder.shopify_order_name || `#${aOrder.shopify_order_id}`}</span>
                {aOrder.customer_name && <span className="font-normal" style={{ color: COLORS.textTertiary }}>{aOrder.customer_name}</span>}
                <button onClick={() => { setAOrder(null); setAOrderSearch(''); }} aria-label={t('Retirer la commande')}
                  className="w-4 h-4 rounded-full flex items-center justify-center" style={{ color: COLORS.textTertiary }}>✕</button>
              </div>
            ) : (
              <>
                <input className={fInput} style={fStyle} placeholder={t('Lier à une commande — n° ou nom du client (optionnel)')}
                  value={aOrderSearch} onChange={(e) => setAOrderSearch(e.target.value)}
                  onFocus={() => setAOrderOpen(true)}
                  onBlur={() => setTimeout(() => setAOrderOpen(false), 150)} />
                {aOrderOpen && (orderMatchesQ.data?.orders?.length ?? 0) > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-lg overflow-hidden shadow-lg max-h-64 overflow-y-auto"
                    style={{ background: COLORS.bgCard, border: `1px solid ${COLORS.borderSubtle}` }}>
                    {!aOrderTerm && (
                      <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: COLORS.textTertiary }}>
                        {t('Commandes récentes')}
                      </p>
                    )}
                    {orderMatchesQ.data!.orders.slice(0, 8).map((o) => (
                      <button key={o.id} type="button" onClick={() => { setAOrder(o); setAOrderSearch(''); setAOrderOpen(false); }}
                        className="w-full text-left px-3 py-2 text-[12px] flex items-baseline gap-2 hover:opacity-80"
                        style={{ color: COLORS.textPrimary }}>
                        <span className="font-semibold">{o.shopify_order_name || `#${o.shopify_order_id}`}</span>
                        <span className="truncate" style={{ color: COLORS.textTertiary }}>{o.customer_name ?? ''}</span>
                        <span className="ml-auto shrink-0 text-[10px]" style={{ color: COLORS.textTertiary }}>
                          {o.total_price ? `${Math.round(Number(o.total_price))} DH` : ''}
                        </span>
                      </button>
                    ))}
                    {orderMatchesQ.isFetching && (
                      <p className="px-3 py-1.5 text-[10px]" style={{ color: COLORS.textTertiary }}>…</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          {addAdvance.isError && <p className="text-[11px] mt-1" style={{ color: COLORS.error }}>{(addAdvance.error as Error).message}</p>}
        </div>

        {/* Ajustement manuel */}
        <div className="rounded-xl p-4" style={blockStyle}>
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2.5" style={{ color: COLORS.textTertiary }}>{t('+ Ajustement manuel')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <input className={fInput} style={fStyle} type="number" step="0.01" placeholder={t('+500 ou −200')} value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} />
            <input className={fInput} style={fStyle} placeholder={t('Libellé')} value={adjLabel} onChange={(e) => setAdjLabel(e.target.value)} />
            <Button size="sm" onClick={() => addAdjustment.mutate()} loading={addAdjustment.isPending} disabled={!(Number(adjAmount) !== 0 && Number.isFinite(Number(adjAmount)) && adjLabel.trim())}>{t('Ajouter')}</Button>
          </div>
          <p className="text-[11px] mt-1.5" style={{ color: COLORS.textTertiary }}>{t('Montant signé : + entrée d’espèces, − sortie.')}</p>
          {addAdjustment.isError && <p className="text-[11px] mt-1" style={{ color: COLORS.error }}>{(addAdjustment.error as Error).message}</p>}

          {adjustments.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {adjustments.map((a: CashAdjustment) => (
                <div key={a.id} className="flex items-center gap-2 text-[12px]">
                  <span style={{ color: COLORS.textTertiary }}>{a.entry_date}</span>
                  <span className="font-semibold" style={{ color: moneyColor(a.amount) }}>{a.amount >= 0 ? '+' : '−'}{madPrecise(Math.abs(a.amount))}</span>
                  <span className="truncate" style={{ color: COLORS.textSecondary }}>{a.label}</span>
                  {a.order && (
                    <span className="shrink-0 font-semibold" style={{ color: '#C9A24A' }}
                      title={a.order.customer_name ?? undefined}>
                      · {a.order.shopify_order_name || `#${a.order.shopify_order_id}`}
                    </span>
                  )}
                  {a.source === 'auto' && (
                    <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5"
                      style={{ background: 'rgba(201,162,74,.12)', color: 'var(--color-adm-gold-ink)' }}
                      title={t('Créée automatiquement depuis la commande (acompte / paiement confirmé)')}>
                      auto
                    </span>
                  )}
                  <button onClick={() => delAdjustment.mutate(a.id)} className={`${miniBtn} ml-auto`} style={{ borderColor: COLORS.borderSubtle, color: COLORS.error }} aria-label={t('Supprimer')}>
                    <Icon d={ICONS.trash} className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function AdminAccountingPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'accounting' | 'marketing'>('accounting');
  const [dates, setDates] = useState<DateRange>(() => presetRange('30d'));
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [showScan, setShowScan] = useState(false);
  const [filterKind, setFilterKind] = useState<ExpenseKind | ''>('');
  const [targetDraft, setTargetDraft] = useState<string>('');
  const [showSpend, setShowSpend] = useState(false);
  const [showCaisse, setShowCaisse] = useState(false);

  const rangeKey = `${dates.from ?? 'all'}_${dates.to ?? 'now'}`;

  const summaryQ = useQuery({
    queryKey: ['accounting-summary', rangeKey],
    queryFn: () => accountingApi.summary(dates),
  });
  const expensesQ = useQuery({
    queryKey: ['accounting-expenses', rangeKey, filterKind],
    queryFn: () => accountingApi.listExpenses({ ...dates, kind: filterKind || undefined }),
  });

  const saveTarget = useMutation({
    mutationFn: (v: number) => accountingApi.putTargets({ daily_profit_target: v }),
    onSuccess: () => {
      setTargetDraft('');
      qc.invalidateQueries({ queryKey: ['accounting-summary'] });
    },
  });
  const deleteExpense = useMutation({
    mutationFn: (id: string) => accountingApi.deleteExpense(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounting-summary'] });
      qc.invalidateQueries({ queryKey: ['accounting-expenses'] });
    },
  });

  const s = summaryQ.data?.summary;
  const expenses = expensesQ.data?.expenses ?? [];

  return (
    <AdminLayout
      title={t('Comptabilité')}
      subtitle={t('Trésorerie, marges et objectifs — pilotage (pas comptabilité fiscale)')}
      actions={
        tab === 'accounting' ? (
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowScan(true)}>{t('Scanner un reçu')}</Button>
            <Button size="sm" onClick={() => setShowAdd(true)}>{t('+ Ajouter une dépense')}</Button>
          </div>
        ) : null
      }
    >
      <div style={{ background: COLORS.bgPrimary }} className="-m-4 sm:-m-6 p-4 sm:p-6 min-h-full">
        {/* Tab switcher: Comptabilité / Marketing */}
        <div className="flex gap-2 mb-4">
          {([
            { k: 'accounting', label: 'Comptabilité' },
            { k: 'marketing', label: 'Marketing' },
          ] as const).map((tb) => (
            <button
              key={tb.k}
              onClick={() => setTab(tb.k)}
              className="px-4 py-2 rounded-lg text-sm font-semibold border transition-colors min-h-[44px]"
              style={
                tab === tb.k
                  ? { background: COLORS.accentPrimary, borderColor: COLORS.accentPrimary, color: '#FFF' }
                  : { background: COLORS.bgCard, borderColor: COLORS.borderSubtle, color: COLORS.textSecondary }
              }
            >
              {t(tb.label)}
            </button>
          ))}
        </div>

        {/* Date range selector */}
        <div className="flex flex-wrap gap-2 mb-2">
          <DateRangePicker value={dates} onChange={setDates} />
        </div>

        {tab === 'marketing' && <MarketingSection dates={dates} />}

        {tab === 'accounting' && summaryQ.isLoading && <p className="text-sm py-10 text-center" style={{ color: COLORS.textTertiary }}>{t('Chargement…')}</p>}
        {tab === 'accounting' && summaryQ.isError && <p className="text-sm py-10 text-center" style={{ color: COLORS.error }}>{(summaryQ.error as Error).message}</p>}

        {tab === 'accounting' && s && (
          <>
            {/* KPI row */}
            <SectionHeading label={t('Vue d’ensemble')} />
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <KpiCard icon={ICONS.revenue} label={t('Revenu (livré)')} value={mad(s.pnl.revenue)} sub={t('{n} commandes livrées', { n: s.pnl.deliveredCount })} accent delay={0} />
              <KpiCard icon={ICONS.margin} label={t('Marge brute')} value={mad(s.pnl.grossMargin)} color={moneyColor(s.pnl.grossMargin)} sub={t('Revenu − COGS')} delay={60} />
              <KpiCard icon={ICONS.contribution} label={t('Marge de contribution')} value={mad(s.pnl.contributionMargin)} color={moneyColor(s.pnl.contributionMargin)} sub={t('− livraison')} delay={120} />
              <KpiCard icon={ICONS.net} label={t('Bénéfice net')} value={mad(s.pnl.netProfit)} color={moneyColor(s.pnl.netProfit)} sub={t('− fixes − variables')} delay={180} />
              {/* Cash en main — the REAL spendable bank cash (manual entry). Click → Dépenser. */}
              <button
                type="button"
                onClick={() => setShowSpend(true)}
                title={t('Cliquer pour dépenser de la caisse')}
                className="rounded-xl p-5 border relative overflow-hidden adm-rise text-left transition-transform duration-200 hover:-translate-y-0.5"
                style={{ ...cardStyle, animationDelay: '240ms', borderColor: COLORS.accentPrimary }}
              >
                <span aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${COLORS.accentPrimary}, #E0BE73)` }} />
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: COLORS.accentSoft, color: COLORS.accentHover }}>
                    <Icon d={ICONS.cash} />
                  </div>
                  <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: COLORS.accentSoft, color: COLORS.accentHover }}>{t('CAISSE')}</span>
                </div>
                <p className="text-[11px] font-medium uppercase tracking-wider mb-1.5" style={{ color: COLORS.textTertiary }}>{t('Cash en main')}</p>
                <p className="text-[26px] leading-none mb-1" style={{ color: moneyColor(s.cash.total), fontFamily: HEADING, fontWeight: 500, letterSpacing: '-0.01em' }}>{mad(s.cash.total)}</p>
                <p className="text-[11px] mt-1.5" style={{ color: COLORS.accentHover }}>{t('Cliquer pour dépenser →')}</p>
              </button>
            </div>

            {/* Cash en main breakdown + manage button */}
            <div className="mt-4 rounded-xl border p-5 adm-rise" style={{ ...cardStyle, animationDelay: '280ms' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: COLORS.accentSoft, color: COLORS.accentHover }}>
                  <Icon d={ICONS.wallet} className="w-[18px] h-[18px]" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: COLORS.textTertiary }}>{t('Cash en main — d’où ça vient')}</p>
                  <p className="text-xl" style={{ fontFamily: HEADING, color: moneyColor(s.cash.total), fontWeight: 500 }}>{madPrecise(s.cash.total)}</p>
                </div>
                <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setShowCaisse(true)}>{t('Gérer la caisse')}</Button>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[12px]">
                {[
                  { label: t('Versements AMEEX'), value: s.cash.payouts_net, sub: false },
                  { label: t('Avances reçues'), value: s.cash.advances_received, sub: false },
                  { label: t('Ajouts'), value: s.cash.added, sub: false },
                  { label: t('Dépensé'), value: s.cash.spent, sub: true },
                ].map((r, i) => (
                  <span key={i} className="inline-flex items-baseline gap-1.5">
                    <span style={{ color: COLORS.textTertiary }}>{r.label}</span>
                    <span className="font-semibold" style={{ color: r.sub ? COLORS.error : COLORS.textPrimary }}>
                      {r.sub ? '−' : '+'}{mad(Math.abs(r.value))}
                    </span>
                  </span>
                ))}
              </div>
            </div>

            {/* Trend sparkline */}
            {s.byDay.length > 0 && (
              <div className="mt-4 rounded-xl border p-5 adm-rise" style={{ ...cardStyle, animationDelay: '300ms' }}>
                <p className="text-[11px] uppercase tracking-wider font-semibold mb-3" style={{ color: COLORS.textTertiary }}>
                  {t('Contribution par jour')}
                </p>
                <Sparkline byDay={s.byDay} />
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-4 mt-2">
              {/* P&L card */}
              <SectionHeading label={t('Compte de résultat (période)')} />
              <SectionHeading label={t('Trésorerie')} />
              <Card title={t('Compte de résultat')} delay={0}>
                <PnlRow label={t('Revenu (livré)')} value={s.pnl.revenue} />
                <PnlRow label={t('Coût de revient (COGS)')} value={s.pnl.cogs} subtract />
                <PnlRow label={t('Marge brute')} value={s.pnl.grossMargin} strong />
                <PnlRow label={t('Livraison')} value={s.pnl.delivery} subtract />
                <PnlRow label={t('Marge de contribution')} value={s.pnl.contributionMargin} strong />
                <PnlRow label={t('Charges fixes / récurrentes')} value={s.pnl.recurring} subtract />
                <PnlRow label={t('Charges variables')} value={s.pnl.variableManual} subtract />
                <PnlRow label={t('Bénéfice net')} value={s.pnl.netProfit} strong />
                <PnlRow label={t('Coût d’acquisition (pub)')} value={s.pnl.adSpend} subtract hint={t('dépenses publicitaires')} />
                <PnlRow label={t('Bénéfice net après pub')} value={s.pnl.netProfitAfterAd} strong />
                <div className="mt-3 pt-3 border-t" style={{ borderColor: COLORS.borderSubtle }}>
                  <PnlRow label={t('Achats ponctuels — hors P&L mensuel')} value={s.pnl.oneTime} hint={t('capex')} />
                </div>
              </Card>

              {/* Cash flow card */}
              <Card title={t('Flux de trésorerie')} delay={60}>
                <PnlRow label={t('Encaissements (avances + COD)')} value={s.cashflow.cashIn} />
                <div className="pl-4">
                  <PnlRow label={t('dont avances')} value={s.cashflow.advancesIn} />
                  <PnlRow label={t('dont solde COD livré')} value={s.cashflow.codRemainderIn} />
                </div>
                <PnlRow label={t('Décaissements (dépenses)')} value={s.cashflow.cashOut} subtract />
                <p className="text-[11px] pt-2 -mb-1" style={{ color: COLORS.textTertiary }}>
                  {t('Estimations théoriques (avances supposées + COD). Le cash réel est la « Cash en main » ci-dessus.')}
                </p>
                <PnlRow label={t('Cash en main (réel)')} value={s.cash.total} strong hint={t('saisie manuelle')} />
                <div className="mt-3 pt-3 border-t space-y-1" style={{ borderColor: COLORS.borderSubtle }}>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: COLORS.textSecondary }}>{t('Burn mensuel')}</span>
                    <span className="font-semibold" style={{ color: COLORS.textPrimary }}>{mad(s.cashflow.monthlyBurn)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: COLORS.textSecondary }}>{t('Runway')}</span>
                    <span className="font-semibold" style={{ color: COLORS.textPrimary }}>
                      {s.cashflow.runwayMonths != null ? t('{n} mois', { n: s.cashflow.runwayMonths }) : '—'}
                    </span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Per-album averages (period) — reuses the P&L figures; albums =
                delivered orders in the window. Fixed costs stay period-level. */}
            <SectionHeading label={t('Par album (moyenne)')} />
            <div className="rounded-xl border p-5 adm-rise" style={cardStyle}>
              {s.pnl.deliveredCount > 0 ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { label: t('Revenu moyen / album'), sub: t('Panier moyen (AOV)'),
                        value: s.pnl.revenue / s.pnl.deliveredCount, color: COLORS.textPrimary },
                      { label: t('Coût moyen / album'), sub: t('COGS + livraison + pub'),
                        value: (s.pnl.cogs + s.pnl.delivery + s.pnl.adSpend) / s.pnl.deliveredCount, color: COLORS.textPrimary },
                      { label: t('Profit moyen / album'), sub: t('après coût pub'),
                        value: s.pnl.contributionAfterAd / s.pnl.deliveredCount, color: moneyColor(s.pnl.contributionAfterAd) },
                    ].map((m, i) => (
                      <div key={i} className="rounded-lg p-4" style={{ background: COLORS.bgPrimary }}>
                        <p className="text-[11px] font-medium uppercase tracking-wider mb-1.5" style={{ color: COLORS.textTertiary }}>{m.label}</p>
                        <p className="text-[26px] leading-none mb-1" style={{ color: m.color, fontFamily: HEADING, fontWeight: 500, letterSpacing: '-0.01em' }}>{mad(m.value)}</p>
                        <p className="text-[11px]" style={{ color: COLORS.textTertiary }}>{m.sub}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t" style={{ borderColor: COLORS.borderSubtle }}>
                    {[
                      { label: t('Albums (période)'), value: String(s.pnl.deliveredCount) },
                      { label: t('Coût pub / lead (CPL)'), value: s.pnl.leadsCount > 0 ? mad(s.pnl.adSpend / s.pnl.leadsCount) : '—' },
                      { label: t('Coût pub / album'), value: mad(s.pnl.adSpend / s.pnl.deliveredCount) },
                    ].map((m, i) => (
                      <div key={i}>
                        <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.textTertiary }}>{m.label}</p>
                        <p className="text-[16px] tabular-nums" style={{ color: COLORS.textPrimary, fontFamily: HEADING, fontWeight: 500 }}>{m.value}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] mt-3 leading-relaxed" style={{ color: COLORS.textTertiary }}>
                    {t('Profit par album = après coût pub. Les charges fixes / récurrentes')}{' '}
                    <span style={{ color: COLORS.textSecondary, fontWeight: 600 }}>({mad(s.pnl.recurring)})</span>{' '}
                    {t('restent au niveau de la période et ne sont pas réparties par album.')}
                  </p>
                </>
              ) : (
                <p className="text-sm" style={{ color: COLORS.textTertiary }}>{t('Aucun album livré sur la période.')}</p>
              )}
            </div>

            {/* Targets card */}
            <SectionHeading label={t('Objectifs')} />
            <Card title={t('Objectif de bénéfice')} delay={0}>
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>
                    {t('Objectif de bénéfice / jour (MAD)')}
                  </label>
                  <input
                    className={fInput}
                    style={{ ...fStyle, width: 160 }}
                    type="number"
                    min="0"
                    value={targetDraft !== '' ? targetDraft : String(s.targets.dailyProfitTarget)}
                    onChange={(e) => setTargetDraft(e.target.value)}
                  />
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={saveTarget.isPending}
                  disabled={targetDraft === '' || Number(targetDraft) < 0}
                  onClick={() => saveTarget.mutate(Number(targetDraft))}
                >
                  {t('Enregistrer')}
                </Button>
              </div>

              <div className="grid sm:grid-cols-3 gap-4 mb-2">
                <div className="rounded-lg p-3" style={{ background: COLORS.bgElevated }}>
                  <p className="text-[11px] uppercase tracking-wider" style={{ color: COLORS.textTertiary }}>{t('Commandes / jour nécessaires')}</p>
                  <p className="text-xl" style={{ fontFamily: HEADING, color: COLORS.textPrimary }}>
                    {s.targets.ordersPerDayNeeded != null ? s.targets.ordersPerDayNeeded : '—'}
                  </p>
                </div>
                <div className="rounded-lg p-3" style={{ background: COLORS.bgElevated }}>
                  <p className="text-[11px] uppercase tracking-wider" style={{ color: COLORS.textTertiary }}>{t('Contribution moy. / commande')}</p>
                  <p className="text-xl" style={{ fontFamily: HEADING, color: COLORS.textPrimary }}>{mad(s.targets.avgContributionPerOrder)}</p>
                </div>
                <div className="rounded-lg p-3" style={{ background: COLORS.bgElevated }}>
                  <p className="text-[11px] uppercase tracking-wider" style={{ color: COLORS.textTertiary }}>{t('Panier moyen (AOV)')}</p>
                  <p className="text-xl" style={{ fontFamily: HEADING, color: COLORS.textPrimary }}>{mad(s.targets.currentAOV)}</p>
                </div>
              </div>

              <div className="mt-2">
                <ProgressRow label={t('Aujourd’hui')} p={s.targets.progress.today} />
                <ProgressRow label={t('Cette semaine')} p={s.targets.progress.week} />
                <ProgressRow label={t('Ce mois')} p={s.targets.progress.month} />
              </div>
            </Card>

            {/* Expense category breakdown */}
            {s.expenseByCategory.length > 0 && (
              <>
                <SectionHeading label={t('Dépenses par catégorie (période)')} />
                <Card title={t('Répartition')} delay={0}>
                  {(() => {
                    const maxCat = Math.max(...s.expenseByCategory.map((c) => c.amount), 1);
                    return s.expenseByCategory.map((c) => (
                      <div key={c.category} className="py-2">
                        <div className="flex justify-between text-sm mb-1">
                          <span style={{ color: COLORS.textPrimary }}>{CATEGORY_LABELS[c.category] ? t(CATEGORY_LABELS[c.category]) : c.category}</span>
                          <span className="font-semibold" style={{ color: COLORS.textPrimary }}>{mad(c.amount)}</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: COLORS.bgElevated }}>
                          <div className="h-full rounded-full" style={{ width: `${Math.max(4, (c.amount / maxCat) * 100)}%`, background: `linear-gradient(90deg, ${COLORS.accentPrimary}, #E0BE73)` }} />
                        </div>
                      </div>
                    ));
                  })()}
                </Card>
              </>
            )}
          </>
        )}

        {/* Expense list */}
        {tab === 'accounting' && (
        <>
        <SectionHeading label={t('Dépenses')} />
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {(['', 'recurring', 'variable', 'one_time'] as const).map((k) => (
            <button
              key={k || 'all'}
              onClick={() => setFilterKind(k)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
              style={
                filterKind === k
                  ? { background: COLORS.accentSoft, borderColor: COLORS.accentPrimary, color: COLORS.accentHover }
                  : { background: COLORS.bgCard, borderColor: COLORS.borderSubtle, color: COLORS.textSecondary }
              }
            >
              {k === '' ? t('Toutes') : t(KIND_LABELS[k])}
            </button>
          ))}
        </div>

        <div className="rounded-xl border overflow-hidden adm-rise" style={cardStyle}>
          {expensesQ.isLoading && <p className="text-sm p-6 text-center" style={{ color: COLORS.textTertiary }}>{t('Chargement…')}</p>}
          {!expensesQ.isLoading && expenses.length === 0 && (
            <p className="text-sm p-8 text-center" style={{ color: COLORS.textTertiary }}>{t('Aucune dépense sur cette période.')}</p>
          )}
          {expenses.map((e) => (
            <div key={e.id} className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0" style={{ borderColor: COLORS.borderSubtle }}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold truncate" style={{ color: COLORS.textPrimary }}>
                    {CATEGORY_LABELS[e.category] ? t(CATEGORY_LABELS[e.category]) : e.category}
                  </span>
                  <span
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                    style={{ background: COLORS.bgElevated, color: COLORS.textSecondary }}
                  >
                    {t(KIND_LABELS[e.kind])}
                  </span>
                </div>
                <p className="text-[11px] truncate" style={{ color: COLORS.textTertiary }}>
                  {e.expense_date}
                  {e.vendor ? ` · ${e.vendor}` : ''}
                  {e.label ? ` · ${e.label}` : ''}
                </p>
              </div>
              <span className="text-sm font-semibold shrink-0" style={{ color: COLORS.textPrimary }}>{madPrecise(e.amount)}</span>
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => setEditing(e)}
                  className="w-8 h-8 rounded-lg border flex items-center justify-center"
                  style={{ borderColor: COLORS.borderSubtle, color: COLORS.textSecondary }}
                  aria-label={t('Modifier')}
                >
                  <Icon d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>} className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { if (confirm(t('Supprimer cette dépense ?'))) deleteExpense.mutate(e.id); }}
                  className="w-8 h-8 rounded-lg border flex items-center justify-center"
                  style={{ borderColor: COLORS.borderSubtle, color: COLORS.error }}
                  aria-label={t('Supprimer')}
                >
                  <Icon d={<><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></>} className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        </>
        )}
      </div>

      {showAdd && <ExpenseFormModal onClose={() => setShowAdd(false)} />}
      {editing && <ExpenseFormModal existing={editing} onClose={() => setEditing(null)} />}
      {showScan && <ExpenseReceiptModal onClose={() => setShowScan(false)} />}
      {showSpend && <SpendPopover onClose={() => setShowSpend(false)} />}
      {showCaisse && <ManageCaisseModal onClose={() => setShowCaisse(false)} />}
    </AdminLayout>
  );
}
